import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Youtube,
  Heart,
  Shield,
  Truck,
  Star
} from 'lucide-react';
// Footer logo uses the public favicon

const Footer: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50">
      {/* Key Features Section - Only on Homepage */}
      {isHomePage && (
        <div className="bg-white py-2 sm:py-4 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-4 text-xs sm:grid-cols-4 sm:gap-3 sm:text-sm">
              <div className="flex flex-col items-center text-center">
                <div className="w-6 h-6 sm:w-10 sm:h-10 bg-[#da2516] rounded-full flex items-center justify-center mb-0.5 sm:mb-1">
                  <Truck className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="font-medium text-gray-800 mb-0">24/7 Support</h3>
                <p className="text-gray-600">Round the clock assistance</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-6 h-6 sm:w-10 sm:h-10 bg-[#da2516] rounded-full flex items-center justify-center mb-0.5 sm:mb-1">
                  <Heart className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="font-medium text-gray-800 mb-0">Easy Return</h3>
                <p className="text-gray-600">Hassle-free returns</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-6 h-6 sm:w-10 sm:h-10 bg-[#da2516] rounded-full flex items-center justify-center mb-0.5 sm:mb-1">
                  <Shield className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="font-medium text-gray-800 mb-0">Secure Payment</h3>
                <p className="text-gray-600">Safe & secure transactions</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-6 h-6 sm:w-10 sm:h-10 bg-[#da2516] rounded-full flex items-center justify-center mb-0.5 sm:mb-1">
                  <Star className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="font-medium text-gray-800 mb-0">Best Quality</h3>
                <p className="text-gray-600">Premium products only</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Subscription - Only on Homepage */}
      {isHomePage && (
        <div className="bg-[#da2516] py-4 sm:py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="text-center md:text-left">
                <h3 className="text-white text-xs sm:text-sm font-semibold mb-0.5">GET LATEST FASHION TRENDS & OFFERS</h3>
                <p className="text-white text-xs opacity-90">Subscribe to our newsletter for exclusive fashion deals</p>
              </div>
              <div className="flex w-full md:w-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 md:w-64 px-2 sm:px-4 py-2.5 sm:py-3 rounded-l-lg border-0 focus:outline-none focus:ring-2 focus:ring-white text-sm sm:text-base"
                />
                <button className="bg-white text-[#da2516] px-3 sm:px-6 py-2.5 sm:py-3 rounded-r-lg font-medium hover:bg-gray-100 transition-colors text-sm sm:text-base min-h-[44px]">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="bg-[#003893] text-white py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Company Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <img 
                  src="/favicon.ico"
                  alt="Baakas" 
                  className="w-6 h-6 sm:w-8 sm:h-8"
                  loading="lazy"
                  decoding="async"
                  width={32}
                  height={32}
                />
                <span className="text-lg sm:text-xl font-semibold">Baakas</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Nepal's premier fashion marketplace connecting you with quality products from verified sellers. 
                Experience seamless shopping with fast delivery and exceptional customer service.
              </p>
              <div className="flex space-x-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61577919410179"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-300 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <button
                  type="button"
                  className="text-gray-400 hover:text-blue-300 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Twitter"
                  disabled
                >
                  <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <a 
                  href="https://www.instagram.com/baakasnp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-300 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <button
                  type="button"
                  className="text-gray-400 hover:text-blue-300 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="YouTube"
                  disabled
                >
                  <Youtube className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <span className="text-gray-300 text-sm">
                    Careers
                  </span>
                </li>
              </ul>
            </div>

            {/* Help & Support */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3">Help & Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/help" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Shipping
                  </Link>
                </li>
                <li>
                  <Link to="/returns" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Returns
                  </Link>
                </li>
                <li>
                  <Link to="/orders" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    My Orders
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a href="https://legal.baakas.com.np" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Legal Documents
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-blue-300 transition-colors text-sm">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3">Contact Info</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-blue-300" />
                  <span className="text-gray-300 text-sm">Dependra Chowk, Itahari, Nepal</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-blue-300" />
                  <span className="text-gray-300 text-sm whitespace-nowrap">+977 9761017074, +977 9761017116, +977 9761017117</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-blue-300" />
                  <span className="text-gray-300 text-sm">support@baakas.com.np</span>
                </div>
              </div>
            </div>
          </div>

          {/* App Download Section - Right after Contact Info */}
          <div className="mt-6 pt-6 border-t border-blue-600 w-full">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a
                href="https://apps.apple.com/np/app/baakas-online-shopping-app/id6755933632"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-opacity hover:opacity-80"
                aria-label="Download Baakas app from App Store"
              >
                <img
                  src="/assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                  alt="Download on the App Store"
                  className="h-10 sm:h-12 w-auto"
                  loading="lazy"
                  width="180"
                  height="54"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    // Fallback to local badge with invert filter if external fails
                    target.src = "/images/app-store-badge-white.svg";
                    target.className = "h-10 sm:h-12 w-auto brightness-0 invert";
                  }}
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.baakas.buyer&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-opacity hover:opacity-80"
                aria-label="Download Baakas app from Google Play Store"
              >
                <img
                  src="/assets/GetItOnGooglePlay_Badge_Web_color_English.png"
                  alt="Get it on Google Play"
                  className="h-10 sm:h-12 w-auto"
                  loading="lazy"
                  width="180"
                  height="54"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    // Fallback to local badge with invert filter if external fails
                    target.src = "/images/google-play-badge.png";
                    target.className = "h-10 sm:h-12 w-auto brightness-0 invert";
                  }}
                />
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-6 pt-6 border-t border-blue-600">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {currentYear} Baakas. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-2 md:mt-0">
                <a href="https://legal.baakas.com.np" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-300 transition-colors text-sm">
                  Legal Documents
                </a>
                <Link to="/privacy" className="text-gray-400 hover:text-blue-300 transition-colors text-sm">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-gray-400 hover:text-blue-300 transition-colors text-sm">
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 